import styled from '@emotion/styled'
import { Alert, Typography } from '@vivaldis/antd-ui'
import { format, isValid } from 'date-fns'
import { Fragment, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DATES_OF_ADVICE_ITEMS,
  HEALTH_RISKS_ITEMS,
  MATERNITY_PROTECTION_MEASURES_ITEMS,
  REQUIRED_VACCINATIONS_AND_TESTS_ITEMS,
  WORK_CLOTHES_AND_PROTECTIVE_EQUIPMENT_ITEMS
} from '../../../../constants/wpsFormSteps'
import { useAiSuggestion } from '../../../../providers/AiSuggestionProvider'
import {
  useForm,
  WorkPostSheetFormValues
} from '../../../../providers/FormProvider'
import { WorkPostSheetItemTypesEnum } from '../../../../types'
import { isDateWPSItem } from '../../../../utils/isDateWPSItem'
import { isTextWPSItem } from '../../../../utils/isTextWPSItem'

const TEXT_FIELDS: (keyof WorkPostSheetFormValues)[] = [
  'tasksToPerform',
  'requiredQualifications',
  'workEquipment',
  'priorInstructions',
  'foreseeableTraining',
  'workstationLocation',
  'welcomeOfficer',
  'welcomeOfficerPosition'
]

// Read-only summary of everything filled in on steps 1-4.
export const Overview = () => {
  const [t, i18n] = useTranslation('web_ui')

  const { values } = useForm()
  const { hasAiSuggestedValues } = useAiSuggestion()

  const emptyValue = t('work_post_sheet_form.overview.empty')

  const getItemLabel = useCallback(
    (name: WorkPostSheetItemTypesEnum) => {
      const translationKey = `work_post_sheet_form.${name.toLocaleLowerCase()}`
      const label = t(`${translationKey}.label`)
      return i18n.exists(`${translationKey}.code`, { ns: 'web_ui' })
        ? `${label} — ${t(`${translationKey}.code`)}`
        : label
    },
    [i18n, t]
  )

  // same rules as wpsFormValuesToWorkPostSheetInput: date/text items are set by a comment, others by the checkbox
  const getItemValue = useCallback(
    (name: WorkPostSheetItemTypesEnum): string => {
      const item = values.items.find(item => item.name === name)
      const comment = item?.comment

      if (isDateWPSItem(name)) {
        return comment instanceof Date && isValid(comment)
          ? format(comment, 'dd/MM/yyyy')
          : emptyValue
      }
      if (isTextWPSItem(name)) {
        return (comment as string) || emptyValue
      }
      if (!item?.checked) {
        return t('work_post_sheet_form.overview.no')
      }
      return (comment as string) || t('work_post_sheet_form.overview.yes')
    },
    [emptyValue, t, values.items]
  )

  const renderItems = (names: WorkPostSheetItemTypesEnum[]) =>
    names.map(name => (
      <OverviewRow
        key={name}
        label={getItemLabel(name)}
        value={getItemValue(name)}
      />
    ))

  return (
    <>
      {hasAiSuggestedValues ? (
        <Alert
          type="info"
          showIcon
          message={t('work_post_sheet_form.overview.ai_review_alert')}
          style={{ marginBottom: 8 }}
        />
      ) : null}

      <Section>
        {TEXT_FIELDS.map(field => (
          <OverviewRow
            key={field}
            label={t(`work_post_sheet_form.${field}.label`)}
            value={(values[field] as string) || emptyValue}
          />
        ))}
      </Section>

      <Section title={t('work_post_sheet_form.group_1.label')}>
        {renderItems(WORK_CLOTHES_AND_PROTECTIVE_EQUIPMENT_ITEMS)}
      </Section>

      <Section title={t('work_post_sheet_form.group_2.label')}>
        {renderItems(REQUIRED_VACCINATIONS_AND_TESTS_ITEMS)}
      </Section>

      <Section title={t('work_post_sheet_form.group_3.label')}>
        {renderItems(HEALTH_RISKS_ITEMS)}
        <OverviewRow
          label={t('work_post_sheet_form.group_4.label')}
          value={
            values.maternityProtectionMeasuresRequired
              ? t('work_post_sheet_form.overview.yes')
              : t('work_post_sheet_form.overview.no')
          }
        />
        {values.maternityProtectionMeasuresRequired
          ? renderItems(MATERNITY_PROTECTION_MEASURES_ITEMS)
          : null}
      </Section>

      <Section title={t('work_post_sheet_form.group_5.label')}>
        {renderItems(DATES_OF_ADVICE_ITEMS)}
      </Section>
    </>
  )
}

interface SectionProps {
  title?: string
  children: ReactNode
}

const Section = ({ title, children }: SectionProps) => (
  <SectionBase>
    {title ? <SectionTitle>{title}</SectionTitle> : null}
    {children}
  </SectionBase>
)

interface OverviewRowProps {
  label: string
  value: string
}

const OverviewRow = ({ label, value }: OverviewRowProps) => {
  const [t] = useTranslation('web_ui')

  return (
    <Row>
      <RowLabel>{label}</RowLabel>
      <RowValue>
        <Typography.Paragraph
          ellipsis={{
            rows: 2,
            expandable: 'collapsible',
            symbol: expanded => (
              <Fragment>
                {expanded
                  ? t('work_post_sheet_form.overview.see_less')
                  : t('work_post_sheet_form.overview.see_more')}
              </Fragment>
            )
          }}
          style={{ marginBottom: 0, textAlign: 'right' }}
        >
          {value}
        </Typography.Paragraph>
      </RowValue>
    </Row>
  )
}

const SectionBase = styled('div')`
  padding: 12px 0;

  & + & {
    border-top: 1px solid ${props => props.theme.gray5};
  }
`

const SectionTitle = styled('div')`
  padding: 12px 0 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: ${props => props.theme.gray9};
`

const Row = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;

  & + & {
    border-top: 1px solid ${props => props.theme.gray5};
  }
`

const RowLabel = styled('div')`
  flex: 0 1 45%;
  font-size: 14px;
  line-height: 22px;
  color: ${props => props.theme.gray7};
`

const RowValue = styled('div')`
  flex: 1 1 55%;
  min-width: 0;
  font-size: 14px;
  line-height: 22px;
  color: ${props => props.theme.gray10};
  white-space: pre-wrap;
  word-break: break-word;
`
